import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  ChevronDown,
  Layers,
  ScrollText,
  ShieldAlert,
  Zap,
} from "lucide-react";
import type {
  ResolvedRule,
  ResolvedGuardrail,
  SourcesGroupResult,
  ResolvedSourceGroup,
} from "./threadUtils";

export function TurnGovernance({
  rules,
  guardrails = [],
  sourcesGroup,
  onInspectRule,
  onInspectGuardrail,
  onInspectKnowledge,
  onInspectChunk,
}: {
  rules: ResolvedRule[];
  guardrails?: ResolvedGuardrail[];
  sourcesGroup: SourcesGroupResult;
  onInspectRule?: (ruleId: string, rule?: ResolvedRule) => void;
  onInspectGuardrail?: (guardrailId: string, guardrail?: ResolvedGuardrail) => void;
  onInspectKnowledge?: (knowledgeId: string, source?: ResolvedSourceGroup) => void;
  onInspectChunk?: (chunkId: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { groups: sources, totalChunks, knowledgeItemCount, toolCount } = sourcesGroup;
  const rulesCount = rules.length;
  const guardrailsCount = guardrails.length;

  const knowledgeSources = sources.filter((s) => !s.isTool);
  const toolSources = sources.filter((s) => s.isTool);

  if (rulesCount === 0 && guardrailsCount === 0 && sources.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 pt-2 border-t border-stone-200/40 dark:border-stone-800/40 space-y-1.5">
      {/* Subtle Toggle Header */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 py-1 text-xs cursor-pointer text-left group hover:opacity-80 transition"
        title="Toggle rules, guardrails, knowledge, and tools details"
      >
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {guardrailsCount > 0 && (
            <span className="inline-flex items-center gap-1 font-medium text-rose-500 dark:text-rose-400">
              <ShieldAlert className="w-3 h-3 text-rose-500 dark:text-rose-400" />
              <span>
                {guardrailsCount} {guardrailsCount === 1 ? "guardrail" : "guardrails"}{" "}
                {guardrails.some((g) => g.action === "block" || g.status === "blocked") ? "blocked" : "applied"}
              </span>
            </span>
          )}

          {rulesCount > 0 && (
            <>
              {guardrailsCount > 0 && <span className="text-stone-600 select-none">•</span>}
              <span className="inline-flex items-center gap-1 font-medium text-amber-500 dark:text-amber-400">
                <ScrollText className="w-3 h-3 text-amber-500 dark:text-amber-400" />
                <span>
                  {rulesCount} {rulesCount === 1 ? "rule" : "rules"} applied
                </span>
              </span>
            </>
          )}

          {knowledgeItemCount > 0 && (
            <>
              {(guardrailsCount > 0 || rulesCount > 0) && <span className="text-stone-600 select-none">•</span>}
              <span className="inline-flex items-center gap-1 font-medium text-indigo-500 dark:text-indigo-400">
                <BookOpen className="w-3 h-3 text-indigo-500 dark:text-indigo-400" />
                <span>
                  {knowledgeItemCount} {knowledgeItemCount === 1 ? "knowledge item" : "knowledge items"}
                </span>
              </span>
            </>
          )}

          {totalChunks > 0 && (
            <>
              {(guardrailsCount > 0 || rulesCount > 0 || knowledgeItemCount > 0) && (
                <span className="text-stone-600 select-none">•</span>
              )}
              <span className="inline-flex items-center gap-1 font-medium text-cyan-500 dark:text-cyan-400 font-mono text-[11px]">
                <Layers className="w-3 h-3 text-cyan-500 dark:text-cyan-400" />
                <span>
                  {totalChunks} {totalChunks === 1 ? "chunk" : "chunks"} used
                </span>
              </span>
            </>
          )}

          {toolCount > 0 && (
            <>
              {(guardrailsCount > 0 || rulesCount > 0 || knowledgeItemCount > 0 || totalChunks > 0) && (
                <span className="text-stone-600 select-none">•</span>
              )}
              <span className="inline-flex items-center gap-1 font-medium text-emerald-500 dark:text-emerald-400 text-[11px]">
                <Zap className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                <span>
                  {toolCount} {toolCount === 1 ? "tool" : "tools"} used
                </span>
              </span>
            </>
          )}
        </div>

        <ChevronDown
          className={`w-3.5 h-3.5 text-stone-500 group-hover:text-stone-300 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded Governance Content */}
      {isOpen && (
        <div className="space-y-2 pt-1 pl-1 text-xs animate-in fade-in duration-150">
          {/* Guardrails Applied List */}
          {guardrailsCount > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-[11px] font-semibold text-rose-500 dark:text-rose-400">
                <ShieldAlert className="w-3 h-3 text-rose-500 dark:text-rose-400" />
                <span>Guardrails Applied ({guardrailsCount})</span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 pl-4">
                {guardrails.map((g, gIdx) => {
                  const inner = (
                    <>
                      <span className="text-[9px] uppercase font-mono font-semibold text-rose-600 dark:text-rose-400 bg-rose-500/10 px-1 py-0.5 rounded">
                        {g.category.replace(/_/g, " ")}
                      </span>
                      <span className="text-[11px] text-stone-800 hover:text-rose-600 dark:text-stone-300 dark:hover:text-white transition font-medium hover:underline">
                        {g.title}
                      </span>
                      {g.action && (
                        <span
                          className={`text-[9px] uppercase font-mono font-bold px-1.5 py-0.2 rounded border ${
                            g.action === "block" || g.status === "blocked"
                              ? "bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-800"
                              : "bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                          }`}
                        >
                          {g.action}
                        </span>
                      )}
                      {g.similarity_score != null && (
                        <span className="text-[10px] text-stone-400 font-mono">
                          ({(g.similarity_score * 100).toFixed(1)}%)
                        </span>
                      )}
                      {g.id ? (
                        <ArrowUpRight className="w-2.5 h-2.5 text-stone-400 dark:text-stone-500 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition shrink-0" />
                      ) : (
                        <span className="text-[9px] text-stone-500 font-mono">(no ID)</span>
                      )}
                    </>
                  );

                  if (g.id) {
                    if (onInspectGuardrail) {
                      return (
                        <button
                          key={g.id || gIdx}
                          type="button"
                          onClick={() => onInspectGuardrail(g.id!, g)}
                          className="group inline-flex items-center gap-1.5 transition cursor-pointer text-left"
                          title={
                            g.anchor_text
                              ? `Guardrail [${g.category}]: Matched anchor "${g.anchor_text}"`
                              : `Inspect guardrail ${g.title}`
                          }
                        >
                          {inner}
                        </button>
                      );
                    }
                    return (
                      <Link
                        key={g.id || gIdx}
                        to={`/guardrails/${g.id}`}
                        className="group inline-flex items-center gap-1.5 transition"
                        title={
                          g.anchor_text
                            ? `Guardrail [${g.category}]: Matched anchor "${g.anchor_text}"`
                            : `Open guardrail ${g.title}`
                        }
                      >
                        {inner}
                      </Link>
                    );
                  }

                  return (
                    <div
                      key={gIdx}
                      className="inline-flex items-center gap-1.5 text-stone-600 dark:text-stone-400"
                      title={
                        g.anchor_text
                          ? `Guardrail [${g.category}]: Matched anchor "${g.anchor_text}"`
                          : g.title
                      }
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rules Applied List */}
          {rulesCount > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-[11px] font-semibold text-stone-400">
                <ScrollText className="w-3 h-3 text-amber-400" />
                <span>Rules Applied ({rulesCount})</span>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 pl-4">
                {rules.map((rule, rIdx) => {
                  const inner = (
                    <>
                      <span className="text-[9px] uppercase font-mono font-semibold text-amber-600 dark:text-amber-400">
                        {rule.category.replace(/_/g, " ")}:
                      </span>
                      <span className="text-[11px] text-stone-800 hover:text-amber-700 dark:text-stone-300 dark:hover:text-white transition font-medium hover:underline">
                        {rule.title}
                      </span>
                      {rule.id ? (
                        <ArrowUpRight className="w-2.5 h-2.5 text-stone-400 dark:text-stone-500 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition shrink-0" />
                      ) : (
                        <span className="text-[9px] text-stone-500 font-mono">(no ID)</span>
                      )}
                    </>
                  );

                  if (rule.id) {
                    if (onInspectRule) {
                      return (
                        <button
                          key={rule.id || rIdx}
                          type="button"
                          onClick={() => onInspectRule(rule.id!, rule)}
                          className="group inline-flex items-center gap-1 transition cursor-pointer text-left"
                          title={
                            rule.instruction
                              ? `Rule [${rule.category}]: ${rule.instruction}`
                              : `Inspect rule ${rule.title}`
                          }
                        >
                          {inner}
                        </button>
                      );
                    }
                    return (
                      <Link
                        key={rule.id || rIdx}
                        to={`/rules/${rule.id}`}
                        className="group inline-flex items-center gap-1 transition"
                        title={
                          rule.instruction
                            ? `Rule [${rule.category}]: ${rule.instruction}`
                            : `Open rule ${rule.title}`
                        }
                      >
                        {inner}
                      </Link>
                    );
                  }

                  return (
                    <div
                      key={rIdx}
                      className="inline-flex items-center gap-1"
                      title={`Unlinked rule directive: ${rule.title}`}
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Knowledge Sources & Chunks Consulted */}
          {knowledgeSources.length > 0 && (
            <div className="space-y-1 pt-1">
              <div className="flex items-center gap-1 text-[11px] font-semibold text-stone-400">
                <BookOpen className="w-3 h-3 text-indigo-400" />
                <span>
                  Knowledge Sources ({knowledgeSources.length} item{knowledgeSources.length === 1 ? "" : "s"}
                  {totalChunks > 0 ? `, ${totalChunks} chunks` : ""})
                </span>
              </div>
              <div className="space-y-1 pl-4">
                {knowledgeSources.map((source, sIdx) => {
                  const hasChunks = source.chunks.length > 0;

                  return (
                    <div
                      key={source.id || sIdx}
                      className="flex flex-wrap items-center gap-x-2 gap-y-1"
                    >
                      {source.id ? (
                        onInspectKnowledge ? (
                          <button
                            type="button"
                            onClick={() => onInspectKnowledge(source.id!, source)}
                            className="group inline-flex items-center gap-1 hover:underline text-[11px] text-stone-700 hover:text-indigo-600 dark:text-stone-300 dark:hover:text-indigo-300 transition cursor-pointer text-left"
                            title={`Inspect knowledge item ${source.title}`}
                          >
                            {source.category && (
                              <span className="text-[9px] uppercase font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                                {source.category.replace(/_/g, " ")}:
                              </span>
                            )}
                            <span>{source.title}</span>
                            <ArrowUpRight className="w-2.5 h-2.5 text-stone-400 dark:text-stone-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 shrink-0" />
                          </button>
                        ) : (
                          <Link
                            to={`/knowledge/${source.id}`}
                            className="group inline-flex items-center gap-1 hover:underline text-[11px] text-stone-700 hover:text-indigo-600 dark:text-stone-300 dark:hover:text-indigo-300 transition"
                            title={`Open knowledge item ${source.title}`}
                          >
                            {source.category && (
                              <span className="text-[9px] uppercase font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                                {source.category.replace(/_/g, " ")}:
                              </span>
                            )}
                            <span>{source.title}</span>
                            <ArrowUpRight className="w-2.5 h-2.5 text-stone-400 dark:text-stone-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 shrink-0" />
                          </Link>
                        )
                      ) : (
                        <div className="text-[11px] text-stone-700 dark:text-stone-300">
                          {source.category && (
                            <span className="text-[9px] uppercase font-mono font-semibold text-indigo-600 dark:text-indigo-400 mr-1">
                              {source.category.replace(/_/g, " ")}:
                            </span>
                          )}
                          <span>{source.title}</span>
                        </div>
                      )}

                      {/* Explicit Chunk Pills with Links */}
                      {hasChunks && (
                        <div className="inline-flex items-center gap-1 pl-1">
                          <span className="text-[10px] text-stone-500 font-mono select-none">chunks:</span>
                          {source.chunks.map((chunkId, cIdx) => (
                            onInspectChunk ? (
                              <button
                                key={cIdx}
                                type="button"
                                onClick={() => onInspectChunk(chunkId)}
                                className="inline-flex items-center gap-0.5 text-[10px] font-mono text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 dark:hover:text-cyan-200 hover:underline transition cursor-pointer"
                                title={`Inspect vector chunk ${chunkId}`}
                              >
                                <span>#{chunkId.slice(0, 8)}</span>
                                <ArrowUpRight className="w-2 h-2 text-stone-400 dark:text-stone-500" />
                              </button>
                            ) : (
                              <Link
                                key={cIdx}
                                to={`/knowledge/chunk/${chunkId}`}
                                className="inline-flex items-center gap-0.5 text-[10px] font-mono text-cyan-400 hover:text-cyan-200 hover:underline transition"
                                title={`Inspect vector chunk ${chunkId}`}
                              >
                                <span>#{chunkId.slice(0, 8)}</span>
                                <ArrowUpRight className="w-2 h-2 text-stone-500" />
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tools Used Section */}
          {toolSources.length > 0 && (
            <div className="space-y-1 pt-1">
              <div className="flex items-center gap-1 text-[11px] font-semibold text-stone-400">
                <Zap className="w-3 h-3 text-emerald-400" />
                <span>Tools Used ({toolSources.length})</span>
              </div>
              <div className="flex flex-wrap gap-x-2.5 gap-y-1.5 pl-4">
                {toolSources.map((tool, tIdx) => (
                  <div
                    key={tool.id || tIdx}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/60 text-emerald-700 dark:text-emerald-300 text-[11px] font-medium font-mono"
                    title={`Tool executed: ${tool.title}${tool.category ? ` (${tool.category})` : ""}`}
                  >
                    <Zap className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-400 shrink-0" />
                    <span>{tool.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
